import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'
import { createUseStyles } from 'react-jss'
import ChartTableSwitch from './ChartTableSwitch'

const { Title } = Typography

export default function PreAuthorizationStats({ stats }) {
  const [viewMode, setViewMode] = useState('chart')
  const classes = createUseStyles({
    inlineTitle: {
      display: 'inline-block',
    }
  })()

  const formatMonth = (record) => {
    const date = new Date(record.year, record.month - 1)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  const columns = [
    {
      key: 'month',
      title: 'Month',
      render: (_, record) => formatMonth(record),
    },
    {
      key: 'count',
      title: '# of Patients',
      dataIndex: 'count',
    },
    {
      key: 'authorizedCount',
      title: 'Authorized',
      dataIndex: 'authorizedCount',
    },
    {
      key: 'percentage',
      title: 'Authorized %',
      render: (_, record, index) => {
        const pct = record.count ? Math.round((record.authorizedCount / record.count) * 100) : 0
        const prev = stats[index - 1]
        let prevPct = null
        if (prev) {
          prevPct = prev.count ? Math.round((prev.authorizedCount / prev.count) * 100) : 0
        }
        const diff = prevPct !== null ? pct - prevPct : null

        return (
          <span>
            {pct}%
            {diff !== null && diff !== 0 && (
              <span style={{ color: diff > 0 ? '#52c41a' : '#f5222d', marginLeft: 4 }}>
                ({diff > 0 ? '+' : ''}{diff}%)
              </span>
            )}
          </span>
        )
      },
    },
  ]

  const chartOptions = useMemo(() => ({
      chart: {
        type: 'bar',
        sparkline: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
        stacked: true,
      },
      dataLabels: { enabled: false },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
        },
      },
      states: {
        normal: { filter: { type: 'none' } },
      },
      grid: { show: false },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        markers: {
          fillColors: ['#3695dc', '#52c41a'],
        },
      },
      xaxis: {
        categories: stats.map(s => formatMonth(s)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        type: 'category',
      },
      yaxis: {
        labels: {
          formatter(value) {
            return `${Math.round(value)}`
          },
        },
        min: 0,
        title: {
          text: '# of Patients / Authorized',
        },
      },
      colors: ['#52c41a', '#3695dc'],
      tooltip: {
        shared: true,
        intersect: false,
        marker: {
          fillColors: ['#3695dc', '#52c41a'],
        },
        y: {
          formatter(value, { seriesIndex, dataPointIndex }) {
            const s = stats[dataPointIndex]
            if (seriesIndex === 0) {
              return `${s.count}`
            }
            if (seriesIndex === 1) {
              const pct = s.count ? Math.round((s.authorizedCount / s.count) * 100) : 0
              const prev = stats[dataPointIndex - 1]
              let diffHtml = ''
              if (prev) {
                const prevPct = prev.count ? Math.round((prev.authorizedCount / prev.count) * 100) : 0
                const diff = pct - prevPct
                if (diff !== 0) {
                  const color = diff > 0 ? '#52c41a' : '#f5222d'
                  const sign = diff > 0 ? '+' : ''
                  diffHtml = ` <span style="color:${color}">(${sign}${diff}%)</span>`
                }
              }
              return `${s.authorizedCount}${diffHtml}`
            }
            return `${Math.round(value)}`
          },
        },
      },
  }), [stats])

  // Flipped on purpose to show authorized in green and non-authorized in blue
  const chartSeries = [
    {
      name: '# of Patients',
      data: stats.map(s => s.authorizedCount),
    },
    {
      name: 'Authorized',
      data: stats.map(s => s.count - s.authorizedCount),
    },
  ]

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} className={classes.inlineTitle}>
            Pre-Authorization stats
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      <div>
        {viewMode === 'chart' && (
          <Row gutter={[0, 50]}>
            <Col span={24}>
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={350}
              />
            </Col>
          </Row>
        )}
        {viewMode === 'table' && (
          <Table
            columns={columns}
            dataSource={stats}
            rowKey={(record) => `${record.year}-${record.month}`}
            size="small"
            pagination={false}
          />
        )}
      </div>
    </div>
  )
}

PreAuthorizationStats.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      authorizedCount: PropTypes.number.isRequired,
    }),
  ).isRequired,
}
